import React, { useEffect, useState } from 'react';
import { RestrictedAPI } from '../../api';
import {
    CONTACT_CONFIRMATION_ENDPOINT,
    CONTACT_VERIFICATION_ENDPOINT,
    GenericObject,
    translations,
    checkEmail,
    checkPhoneNumber,
    checkIfSerbianPhoneNumber,
} from '../../lib';
import { Button } from '../../components/Button';
import { TextField } from '../../components/TextField';
import { FormControl, FormHelperText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Field, useFormikContext } from 'formik';

type ContactVerificationProps = {
    ucn: string;
    emailShouldVerify: boolean;
    phoneShouldVerify: boolean;
    setContactVerified: (verified: GenericObject) => void;
    phoneFieldName: string;
    emailFieldName: string;
};

type VerificationResponse = {
    emailGuid?: string;
    greska?: string | null;
    phoneGuid?: string;
};

type ConfirmationResponse = {
    greska: string | null;
};

const VERIFICATION_CODE_COOLDOWN_MS = 10 * 60 * 1000;
const VERIFICATION_CODE_REQUESTED_AT_FIELD = 'contactVerificationCodeRequestedAt';
const VERIFICATION_RESPONSE_FIELD = 'contactVerificationResponse';

export const ContactVerification = (props: ContactVerificationProps) => {
    const { ucn, emailShouldVerify, phoneShouldVerify, setContactVerified, phoneFieldName, emailFieldName, ...rest } =
        props;
    const { values, errors, touched, setFieldValue, setTouched, setErrors } = useFormikContext<GenericObject>();

    const [phoneVerified, setPhoneVerified] = useState<boolean>(false);
    const [emailVerified, setEmailVerified] = useState<boolean>(false);
    const [verificationResponse, setVerificationResponse] = useState<VerificationResponse | null>(
        () => values[VERIFICATION_RESPONSE_FIELD] ?? null,
    );
    const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
    const [emailVerificationCode, setEmailVerificationCode] = useState('');
    const [currentTime, setCurrentTime] = useState(Date.now());

    const canVerifyPhone = Boolean(phoneShouldVerify && values[phoneFieldName]);
    const canVerifyEmail = Boolean(emailShouldVerify && values[emailFieldName]);
    const canVerifyContact = Boolean((canVerifyPhone || canVerifyEmail) && CONTACT_VERIFICATION_ENDPOINT);
    const isContactVerified = phoneVerified || emailVerified;
    const confirmationTitle = phoneShouldVerify
        ? translations.contactVerification.PLEASE_CONFIRM_PHONE_NUMBER
        : translations.contactVerification.PLEASE_CONFIRM_EMAIL_ADDRESS;
    const confirmationText = phoneShouldVerify
        ? translations.contactVerification.CONFIRM_PHONE_TEXT
        : translations.contactVerification.CONFIRM_EMAIL_TEXT;
    const verificationCodeInstruction = phoneShouldVerify
        ? translations.contactVerification.CLICK_TO_SEND_VERIFICATION_CODE_TO_PHONE
        : translations.contactVerification.CLICK_TO_SEND_VERIFICATION_CODE_TO_EMAIL;
    const verificationCodeRequestedAt = Number(values[VERIFICATION_CODE_REQUESTED_AT_FIELD]) || 0;
    const cooldownEndsAt = verificationCodeRequestedAt + VERIFICATION_CODE_COOLDOWN_MS;
    const remainingCooldownMs = Math.max(0, cooldownEndsAt - currentTime);
    const remainingCooldownSeconds = Math.floor(remainingCooldownMs / 1000);
    const remainingCooldownTime = `${Math.floor(remainingCooldownSeconds / 60)
        .toString()
        .padStart(2, '0')}:${(remainingCooldownSeconds % 60).toString().padStart(2, '0')}`;
    const isVerificationCodeCooldownActive = remainingCooldownMs > 0;

    useEffect(() => {
        if (!isVerificationCodeCooldownActive) {
            return;
        }

        const timerId = window.setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);

        return () => window.clearInterval(timerId);
    }, [isVerificationCodeCooldownActive]);

    const handleVerifyContact = async () => {
        const existingErrors: GenericObject = {};
        let valid = true;
        const touchedObject: GenericObject = {};
        touchedObject[phoneFieldName] = true;
        touchedObject[emailFieldName] = true;
        setTouched(touchedObject, false);
        if (!canVerifyContact || isContactVerified) {
            return;
        }
        if (
            canVerifyPhone &&
            (!checkIfSerbianPhoneNumber(values[phoneFieldName]) || !checkPhoneNumber(values[phoneFieldName]))
        ) {
            existingErrors[phoneFieldName] = translations.validationMessages.PHONE_NUMBER;
            valid = false;
        }
        if (canVerifyEmail && !checkEmail(values[emailFieldName])) {
            existingErrors[emailFieldName] = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
            valid = false;
        }
        if (valid) {
            try {
                const response = await RestrictedAPI.post<VerificationResponse>(CONTACT_VERIFICATION_ENDPOINT, {
                    jmbg: ucn,
                    email: canVerifyEmail ? values[emailFieldName] : undefined,
                    mobilniTelefon: canVerifyPhone ? values[phoneFieldName] : undefined,
                });

                setVerificationResponse(response.data);
                setFieldValue(VERIFICATION_RESPONSE_FIELD, response.data);
                setFieldValue(VERIFICATION_CODE_REQUESTED_AT_FIELD, Date.now());
                setCurrentTime(Date.now());
            } catch (error) {
                setVerificationResponse({
                    greska:
                        (error as any)?.response?.data?.greska || translations.contactVerification.VERIFICATION_ERROR,
                });
            }
        } else {
            setErrors(existingErrors);
        }
    };

    const handleConfirmPhoneVerification = async () => {
        if (isContactVerified || !verificationResponse?.phoneGuid || !phoneVerificationCode) {
            return;
        }

        await RestrictedAPI.post<ConfirmationResponse>(CONTACT_CONFIRMATION_ENDPOINT, {
            VerifikacijaGUID: verificationResponse.phoneGuid,
            KodZaVerifikaciju: phoneVerificationCode,
        })
            .then((response) => {
                setVerificationResponse((previous) => ({ ...previous, greska: response.data.greska }));
                if (response.data.greska) {
                    return;
                }

                setPhoneVerified(true);
                setFieldValue(VERIFICATION_CODE_REQUESTED_AT_FIELD, 0);
                const contactVerified: GenericObject = { phoneGuid: verificationResponse.phoneGuid };
                if (emailShouldVerify && emailVerified) {
                    contactVerified.phoneGuid = verificationResponse.phoneGuid;
                }
                setContactVerified(contactVerified);
            })
            .catch((error) => {
                setVerificationResponse((previous) => ({
                    ...previous,
                    greska: error?.response?.data?.greska || translations.contactVerification.VERIFICATION_ERROR,
                }));
                setPhoneVerified(false);
            });
    };

    const handleConfirmEmailVerification = async () => {
        if (isContactVerified || !verificationResponse?.emailGuid || !emailVerificationCode) {
            return;
        }

        await RestrictedAPI.post<ConfirmationResponse>(CONTACT_CONFIRMATION_ENDPOINT, {
            VerifikacijaGUID: verificationResponse.emailGuid,
            KodZaVerifikaciju: emailVerificationCode,
        })
            .then((response) => {
                setVerificationResponse((previous) => ({ ...previous, greska: response.data.greska }));
                if (response.data.greska) {
                    return;
                }

                setEmailVerified(true);
                setFieldValue(VERIFICATION_CODE_REQUESTED_AT_FIELD, 0);
                const contactVerified: GenericObject = { emailGuid: verificationResponse.emailGuid };
                if (phoneShouldVerify && phoneVerified) {
                    contactVerified.phoneGuid = verificationResponse.phoneGuid;
                }
                setContactVerified(contactVerified);
            })
            .catch((error) => {
                setVerificationResponse((previous) => ({
                    ...previous,
                    greska: error?.response?.data?.greska || translations.contactVerification.VERIFICATION_ERROR,
                }));
                setEmailVerified(false);
            });
    };

    return (
        <div {...rest} style={{ maxWidth: '50%', width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <h2
                        style={{ margin: 0, marginTop: '16px', marginBottom: '4px', fontSize: '22px', fontWeight: 600 }}
                    >
                        {translations.contactVerification.VERIFICATION_COMPONENT_TITLE}
                    </h2>

                    <div style={{ fontSize: '14px' }}>{confirmationTitle}</div>
                    <div style={{ fontSize: '12px' }}>{confirmationText}</div>
                    <div style={{ color: '#b71c1c', fontWeight: 'bold', marginTop: '8px', fontSize: '14px' }}>
                        {translations.contactVerification.VERIFICATION_IS_MANDATORY}
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        padding: '16px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#fafafa',
                        marginTop: '8px',
                        marginLeft: '32px',
                    }}
                >
                    <div style={{ fontSize: '12px', marginLeft: '8px' }}>{verificationCodeInstruction}</div>
                    {phoneShouldVerify ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '16px',
                                    width: '100%',
                                    alignItems: 'flex-start',
                                    marginBottom: '8px',
                                }}
                            >
                                <FormControl style={{ flex: 1 }}>
                                    <Field
                                        name={phoneFieldName}
                                        label={translations.contactVerification.PHONE_NUMBER}
                                        value={values[phoneFieldName] ?? ''}
                                        readOnly
                                        as={TextField}
                                    />
                                    {touched[phoneFieldName] && errors[phoneFieldName] ? (
                                        <FormHelperText error={true}>
                                            <>{errors[phoneFieldName]}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                                {phoneVerified ? (
                                    <CheckCircleIcon
                                        aria-label="verified"
                                        color="success"
                                        style={{ marginTop: '16px' }}
                                    />
                                ) : null}
                            </div>
                            <Button
                                onClickHandler={handleVerifyContact}
                                label={translations.contactVerification.VERIFY}
                                disabled={isContactVerified || !canVerifyContact || isVerificationCodeCooldownActive}
                                fullWidth
                                style={{ height: '56px', marginBottom: '8px' }}
                            />
                            {verificationResponse?.phoneGuid ? (
                                <div
                                    style={{
                                        color: '#2e7d32',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        marginBottom: '8px',
                                    }}
                                >
                                    {translations.contactVerification.VERIFICATION_CODE_SENT_TO_PHONE}
                                </div>
                            ) : null}
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                    <TextField
                                        name="phoneVerificationCode"
                                        label={translations.contactVerification.PHONE_CODE}
                                        value={phoneVerificationCode}
                                        onChange={(e) => setPhoneVerificationCode(e.target.value)}
                                        disabled={isContactVerified || !verificationResponse?.phoneGuid}
                                        inputProps={{ maxLength: 6 }}
                                        style={{ flex: 1, height: '56px' }}
                                    />
                                    <div style={{ flex: 1, height: '56px' }}>
                                        <Button
                                            onClickHandler={handleConfirmPhoneVerification}
                                            label={translations.contactVerification.CONFIRM}
                                            disabled={
                                                isContactVerified ||
                                                !verificationResponse?.phoneGuid ||
                                                !phoneVerificationCode
                                            }
                                            fullWidth
                                            style={{ height: '100%' }}
                                        />
                                    </div>
                                </div>
                                {isVerificationCodeCooldownActive ? (
                                    <div style={{ fontSize: '12px', lineHeight: '16px' }}>
                                        {translations.contactVerification.NEW_CODE_AVAILABLE_IN.replace(
                                            '{time}',
                                            remainingCooldownTime,
                                        )}
                                    </div>
                                ) : null}
                            </div>
                            {phoneVerified ? (
                                <div
                                    style={{
                                        color: '#2e7d32',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        marginBottom: '8px',
                                    }}
                                >
                                    {translations.contactVerification.VERIFICATION_SUCCESS_PHONE}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    {emailShouldVerify ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '16px',
                                    width: '100%',
                                    alignItems: 'flex-start',
                                    marginBottom: '8px',
                                }}
                            >
                                <FormControl style={{ flex: 1 }}>
                                    <Field
                                        name={emailFieldName}
                                        label={translations.contactVerification.EMAIL_ADDRESS}
                                        value={values[emailFieldName] ?? ''}
                                        readOnly
                                        disabled={isContactVerified}
                                        as={TextField}
                                    />
                                    {touched[emailFieldName] && errors[emailFieldName] ? (
                                        <FormHelperText error={true}>
                                            <>{errors[emailFieldName]}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                                {emailVerified ? (
                                    <CheckCircleIcon
                                        aria-label="verified"
                                        color="success"
                                        style={{ marginTop: '16px' }}
                                    />
                                ) : null}
                            </div>
                            <Button
                                onClickHandler={handleVerifyContact}
                                label={translations.contactVerification.VERIFY}
                                disabled={isContactVerified || !canVerifyContact || isVerificationCodeCooldownActive}
                                fullWidth
                                style={{ height: '56px', marginBottom: '8px' }}
                            />
                            {verificationResponse?.emailGuid ? (
                                <div
                                    style={{
                                        color: '#2e7d32',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        marginBottom: '8px',
                                    }}
                                >
                                    {translations.contactVerification.VERIFICATION_CODE_SENT_TO_EMAIL}
                                </div>
                            ) : null}
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                    <TextField
                                        name="emailVerificationCode"
                                        label={translations.contactVerification.EMAIL_CODE}
                                        value={emailVerificationCode}
                                        onChange={(e) => setEmailVerificationCode(e.target.value)}
                                        disabled={isContactVerified || !verificationResponse?.emailGuid}
                                        inputProps={{ maxLength: 6 }}
                                        style={{ flex: 2, height: '56px' }}
                                    />
                                    <div style={{ flex: 1, height: '56px' }}>
                                        <Button
                                            onClickHandler={handleConfirmEmailVerification}
                                            label={translations.contactVerification.CONFIRM}
                                            disabled={
                                                isContactVerified ||
                                                !verificationResponse?.emailGuid ||
                                                !emailVerificationCode
                                            }
                                            fullWidth
                                            style={{ height: '100%' }}
                                        />
                                    </div>
                                </div>
                                {isVerificationCodeCooldownActive ? (
                                    <div style={{ fontSize: '12px', lineHeight: '16px' }}>
                                        {translations.contactVerification.NEW_CODE_AVAILABLE_IN.replace(
                                            '{time}',
                                            remainingCooldownTime,
                                        )}
                                    </div>
                                ) : null}
                            </div>
                            {emailVerified ? (
                                <div
                                    style={{
                                        color: '#2e7d32',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        marginTop: '8px',
                                        marginBottom: '8px',
                                    }}
                                >
                                    {translations.contactVerification.VERIFICATION_SUCCESS_EMAIL}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                {verificationResponse?.greska ? (
                    <div style={{ color: '#b71c1c', fontSize: '14px' }}>{verificationResponse.greska}</div>
                ) : null}
            </div>
        </div>
    );
};
